import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import { getArtist, getJournal, getVideos } from '@/lib/api';
import { VideoThumb } from './Videos';
import StructuredData, { artistLd } from '@/components/StructuredData';
import PictureImg from '@/components/PictureImg';
import { getArtworkAspectRatio } from '@/lib/utils';

const fmtDate = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

export default function ArtistDetail() {
    const { slug } = useParams();
    const reveal = useReveal();
    const [data, setData] = useState(null);
    const [journal, setJournal] = useState([]);
    const [films, setFilms] = useState([]);

    useEffect(() => {
        getArtist(slug).then(setData);
        getJournal().then(setJournal);
        getVideos({ artist: slug }).then(setFilms);
    }, [slug]);

    useDocumentMeta({
        title: data ? `${data.artist.name} · Maison Touré` : 'Artist · Maison Touré',
        description: data ? `${data.artist.name} — ${data.artist.origin}. Represented by Maison Touré, Sydney.` : undefined,
    });

    if (!data) {
        return (
            <div style={{ paddingTop: 200 }} className="max-w-[1600px] mx-auto px-8 md:px-16">
                <p className="serif-italic" style={{ fontSize: 22, color: 'var(--ink-2)' }}>Loading…</p>
            </div>
        );
    }
    const { artist, artworks } = data;

    /* Pick journal entries whose body/title/excerpt references the artist name */
    const related = journal.filter((j) =>
        (j.title + ' ' + j.excerpt + ' ' + j.body).toLowerCase().includes(artist.name.toLowerCase())
    );

    return (
        <div ref={reveal} data-testid="artist-detail-page" style={{ paddingTop: 104 }}>
            <StructuredData data={artistLd(artist)} />
            {/* HEADER */}
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12">
                <Link to="/artists" className="overline" style={{ color: 'var(--ink-3)' }}>The Artists</Link>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
                    <div className="md:col-span-5 reveal">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>Artist</span>
                        <h1
                            className="mt-6"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(40px, 5.3vw, 90px)',
                                lineHeight: 0.98,
                                letterSpacing: '-0.025em',
                            }}
                        >
                            {artist.name}
                        </h1>
                        <div
                            className="mt-5"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontStyle: 'italic',
                                fontSize: 20,
                                color: 'var(--ink-2)',
                            }}
                        >
                            {artist.origin} &nbsp;·&nbsp; {artist.born}
                        </div>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 reveal">
                        <div className="img-wrap aspect-[3/4]" style={{ background: 'var(--bg-3)' }}>
                            <PictureImg src={artist.portrait} alt={artist.name} />
                        </div>
                    </div>
                </div>
            </section>

            {/* STATEMENT */}
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-3">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>I &nbsp;—&nbsp; Statement</span>
                    </div>
                    <div className="md:col-span-8 md:col-start-5 reveal">
                        <blockquote
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(26px, 2.6vw, 42px)',
                                lineHeight: 1.28,
                                letterSpacing: '-0.012em',
                                color: 'var(--ink)',
                            }}
                        >
                            <em className="serif-italic" style={{ color: 'var(--ink-3)' }}>&ldquo;</em>
                            {artist.statement}
                            <em className="serif-italic" style={{ color: 'var(--ink-3)' }}>&rdquo;</em>
                        </blockquote>
                    </div>
                </div>
            </section>

            {/* BIOGRAPHY */}
            <section className="rule-top">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-3">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>II &nbsp;—&nbsp; Biography</span>
                    </div>
                    <div className="md:col-span-8 md:col-start-5 reveal">
                        <p
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 19,
                                lineHeight: 1.8,
                                color: 'var(--ink-2)',
                            }}
                        >
                            {artist.biography}
                        </p>
                    </div>
                </div>
            </section>

            {/* SELECTED WORKS */}
            {artworks.length > 0 && (
                <section className="rule-top" data-testid="artist-available-works">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20 md:mb-24">
                            <div>
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>III &nbsp;—&nbsp; Selected Works</span>
                                <h2
                                    className="serif mt-6"
                                    style={{ fontSize: 'clamp(36px, 4.6vw, 72px)', letterSpacing: '-0.02em', lineHeight: 1.04 }}
                                >
                                    Works <em className="serif-italic">available.</em>
                                </h2>
                            </div>
                            <span className="overline" style={{ color: 'var(--ink-3)' }}>
                                {artworks.length} {artworks.length === 1 ? 'work' : 'works'}
                            </span>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-20">
                            {artworks.map((w, i) => (
                                <Link
                                    to={`/artworks/${w.slug}`}
                                    key={w.slug}
                                    className={`reveal group block ${i % 3 === 1 ? 'md:mt-16' : ''}`}
                                    data-testid={`artist-artwork-${w.slug}`}
                                >
                                    <div className="img-wrap img-wrap--natural" style={{ background: 'var(--bg-3)', aspectRatio: getArtworkAspectRatio(w) }} onContextMenu={(e) => e.preventDefault()}>
                                        <PictureImg src={w.image} alt={w.title} loading="lazy" className="no-save" />
                                    </div>
                                    <div className="mt-7">
                                        <h3
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(22px, 2.2vw, 30px)',
                                                lineHeight: 1.12,
                                                letterSpacing: '-0.012em',
                                            }}
                                        >
                                            {w.title}, <span style={{ color: 'var(--ink-3)' }}>{w.year}</span>
                                        </h3>
                                        <div
                                            className="mt-3"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontSize: 14,
                                                color: 'var(--ink-3)',
                                            }}
                                        >
                                            {w.medium.split(',')[0]}
                                        </div>
                                        <div
                                            className="mt-3 overline"
                                            style={{ color: 'var(--ink-2)' }}
                                        >
                                            {w.price_on_request ? 'On Request' : `${w.currency} ${w.price?.toLocaleString()}`}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* EXHIBITIONS + COLLECTIONS + PUBLICATIONS */}
            <section className="rule-top">
                <div
                    className={`max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 gap-16 md:gap-24 ${
                        (artist.collections || []).length > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'
                    }`}
                >
                    <div className="reveal">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>IV &nbsp;—&nbsp; Selected Exhibitions</span>
                        <ul className="mt-10 flex flex-col">
                            {artist.exhibitions.map((e, i) => (
                                <li
                                    key={i}
                                    className="rule-bottom py-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 19,
                                        lineHeight: 1.45,
                                        color: 'var(--ink)',
                                    }}
                                >
                                    {e}
                                </li>
                            ))}
                        </ul>
                    </div>
                    {(artist.collections || []).length > 0 && (
                        <div className="reveal" data-testid="artist-collections">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>V &nbsp;—&nbsp; Selected Collections</span>
                            <ul className="mt-10 flex flex-col">
                                {artist.collections.map((c, i) => (
                                    <li
                                        key={i}
                                        className="rule-bottom py-5"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontWeight: 400,
                                            fontSize: 19,
                                            lineHeight: 1.45,
                                            color: 'var(--ink)',
                                        }}
                                    >
                                        {c}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="reveal">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>
                            {(artist.collections || []).length > 0 ? 'VI' : 'V'} &nbsp;—&nbsp; Publications
                        </span>
                        <ul className="mt-10 flex flex-col">
                            {artist.publications.map((p, i) => (
                                <li
                                    key={i}
                                    className="rule-bottom py-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontWeight: 400,
                                        fontSize: 19,
                                        lineHeight: 1.45,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* RELATED FILMS */}
            {films.length > 0 && (
                <section className="rule-top" data-testid="artist-films-related">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-16">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>From the Film Archive</span>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16">
                            {films.slice(0, 3).map((v) => (
                                <Link to={`/film/${v.slug}`} key={v.slug} className="reveal group block">
                                    <VideoThumb video={v} />
                                    <div className="mt-6">
                                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{v.category}</span>
                                        <h3
                                            className="mt-3"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(20px, 1.8vw, 26px)',
                                                lineHeight: 1.18,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {v.title}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* RELATED JOURNAL */}
            {related.length > 0 && (
                <section className="rule-top" data-testid="artist-journal-related">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-20">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>VI &nbsp;—&nbsp; From the Journal</span>
                            <h2
                                className="serif mt-6"
                                style={{ fontSize: 'clamp(36px, 4.6vw, 72px)', letterSpacing: '-0.02em', lineHeight: 1.04 }}
                            >
                                Further <em className="serif-italic">reading.</em>
                            </h2>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-20">
                            {related.slice(0, 3).map((j) => (
                                <Link to={`/journal/${j.slug}`} key={j.slug} className="reveal group block">
                                    <div className="img-wrap aspect-[4/3]" style={{ background: 'var(--bg-3)' }}>
                                        <PictureImg src={j.cover_image} alt={j.title} loading="lazy" />
                                    </div>
                                    <div className="mt-7">
                                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{j.category}</span>
                                        <h3
                                            className="mt-4"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(22px, 2vw, 28px)',
                                                lineHeight: 1.2,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {j.title}
                                        </h3>
                                        <div
                                            className="mt-4"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontSize: 13,
                                                color: 'var(--ink-3)',
                                            }}
                                        >
                                            {fmtDate(j.published_at)}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
